import { useEffect, useRef, useState } from "react";
import type { Lead } from "../../hooks/useLeadInbox";
import { PlayersBadge } from "../../components/shared";
import { api } from "../../api/client";
import css from "./dcSections.module.css";
import done from "./leadDone.module.css";

/**
 * DC «Лиды» (screen-04). Tenant-wide lead inbox (no branch filter). Cards
 * degrade gracefully (A13): no name → phone is the title; no players/note →
 * blocks are omitted. Source drives the badge colour. Data is owned by the
 * host (DcGridScreen) so the section-tab pill reflects the same count.
 */
export function DcLeadsSection({ leads, loading, error, onChanged, onToast }: { leads: Lead[]; loading: boolean; error: string | null; onChanged?: () => void; onToast?: (m: string) => void }) {
  /* 15.10: вход во вкладку — свежий список (карточка в Telegram ведёт сюда); при обновлении карточки остаются на месте */
  useEffect(() => { onChanged?.(); }, []);   // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className={css.main}>
      <div className={css.addRow}>
        <button type="button" className={css.btnAdd} disabled title="Создание лида — следующий шаг">
          <svg viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square" aria-hidden="true"><path d="M5 1v8M1 5h8" /></svg>
          Лид
        </button>
      </div>

      {error && leads.length === 0 ? (
        <div className={css.err}>Ошибка загрузки лидов: {error}</div>
      ) : loading && leads.length === 0 ? (
        <div className={css.loading}>Загрузка…</div>
      ) : leads.length === 0 ? (
        <div className={css.empty}>Лидов нет</div>
      ) : (
        <>
          <div className={css.secHead}>
            Без даты <span className={css.cnt}>({leads.length})</span>
          </div>
          {leads.map((l) => <LeadCard key={String(l.id)} lead={l} onChanged={onChanged} onToast={onToast} />)}
        </>
      )}
    </div>
  );
}

function LeadCard({ lead, onChanged, onToast }: { lead: Lead; onChanged?: () => void; onToast?: (m: string) => void }) {
  const name = (lead.client_name ?? "").trim();
  const phone = (lead.phone ?? "").trim();
  const title = name || phone || "Без имени";
  const src = sourceMeta(lead.source);
  return (
    <div className={css.lead}>
      <div className={css.leadHead}>
        <div className={css.leadNameRow}>
          <span className={`${css.leadName} ${!name && phone ? css.phoneOnly : ""}`}>{title}</span>
          {/* 05.09 (владелец): лид — брони ещё нет, бейдж игроков только индикатор */}
          {lead.players ? <PlayersBadge count={lead.players} size="sm" /> : null}
        </div>
        <span className={`${css.sourceBadge} ${src.cls}`} title={lead.source ?? undefined}>{src.label}</span>
      </div>
      {(name && phone) || lead.created_at ? (
        <div className={css.leadMeta}>
          {name && phone ? <span className={css.phone}>{phone}</span> : null}
          {lead.created_at ? <span className={css.when}>{fmtWhen(lead.created_at)}</span> : null}
        </div>
      ) : null}
      {lead.comment ? <div className={css.leadNote}>{lead.comment}</div> : null}
      {onChanged ? <LeadDone id={lead.id} onChanged={onChanged} onToast={onToast} /> : null}
    </div>
  );
}

/* 15.10 (заявки сайта в «Лиды», вариант «В»): DC отмечает лид обработанным — строка уходит из «Без даты»
   (сервер: DELETE /api/lead-inbox/:id — мягкое снятие, запись остаётся). Второе нажатие подтверждает.
   texts — 29.10: в «Новом годе» обработанная заявка остаётся в карточке клиента, «убран из списка» там неправда. */
export function LeadDone({ id, onChanged, onToast, texts }: {
  id: string | number; onChanged: () => void; onToast?: (m: string) => void; texts?: { toast: string; ask: string };
}) {
  const [ask, setAsk] = useState(false);
  const [busy, setBusy] = useState(false);
  const armedAt = useRef(0);
  useEffect(() => { if (!ask) return; const t = window.setTimeout(() => setAsk(false), 4000); return () => window.clearTimeout(t); }, [ask]);
  const click = async () => {
    if (!ask) { armedAt.current = Date.now(); setAsk(true); return; }
    if (Date.now() - armedAt.current < 500) return;   // двойной тап — не подтверждение
    setBusy(true);
    try { await api(`/api/lead-inbox/${encodeURIComponent(String(id))}`, { method: "DELETE" }); onToast?.(texts?.toast ?? "Лид обработан — убран из списка"); onChanged(); }
    catch (e) { onToast?.(e instanceof Error ? e.message : "Не получилось — попробуйте ещё раз"); setBusy(false); setAsk(false); onChanged(); }
  };
  return (
    <div className={done.row}>
      <button type="button" className={`${done.btn} ${ask ? done.ask : ""}`} onClick={() => { void click(); }} disabled={busy}>
        {busy ? "…" : ask ? (texts?.ask ?? "Точно убрать из списка?") : "✓ Обработан"}
      </button>
    </div>
  );
}

function sourceMeta(raw?: string | null): { label: string; cls: string } {
  const s = (raw ?? "").toLowerCase();
  /* 15.10 «Сайт НГ | page=… | utm_…»: первым — дальше по тексту страницы и UTM (его пишет посетитель) ярлык не выбираем */
  if (s.startsWith("сайт")) return { label: (raw ?? "").split("|")[0]!.trim(), cls: css.widget ?? "" };
  if (s.includes("улиц")) return { label: "С улицы", cls: "" };
  if (s.includes("звон")) return { label: "Звонок", cls: "" };
  if (s.includes("повтор")) return { label: "Повторник", cls: css.repeat ?? "" };
  if (s.includes("реком")) return { label: "Рекомендация", cls: css.rec ?? "" };
  if (s.includes("виджет") || s.includes("widget")) return { label: "Виджет", cls: css.widget ?? "" };
  return { label: raw?.trim() || "—", cls: "" };
}

function fmtWhen(iso: string): string {
  /* created_at — UTC (datetime('now')); показываем по Оренбургу (UTC+5) с временем: ночная заявка — своим днём */
  const t = Date.parse(iso.replace(" ", "T") + (/[zZ]|[+-]\d\d:?\d\d$/.test(iso) ? "" : "Z"));
  if (Number.isNaN(t)) return iso.slice(0, 10) || iso;
  return new Date(t + 5 * 3600000).toISOString().slice(0, 16).replace("T", " ");
}
